import { EditorLoading } from '@affine/component/page-detail-skeleton';
import { HelpActionsService } from '@affine/core/modules/help-actions';
import { ViewService } from '@affine/core/modules/workbench/services/view';
import { endCoverTiming } from '@affine/core/modules/workbench/view/sidebar-cover';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import type { DocFrontendDocState } from '@affine/nbstore';
import {
  isStreamLainMotionEnabled,
  streamLainAnimationController,
  subscribeStreamLainMotionPolicy,
} from '@blocksuite/affine/shared/utils';
import { cssVarV2 } from '@streamlain/theme/v2';
import { useLiveData, useServiceOptional } from '@toeverything/infra';
import {
  createContext,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import {
  type DocumentLoadStep,
  formatDocumentLoadDiagnostics,
} from './document-load-diagnostics';

type DocumentLoadRecord = {
  key: string;
  startedAt: number;
  reached: Map<DocumentLoadStep, number>;
};

type LoadMask = {
  pageId: string;
  ready: (pageId: string) => void;
  failed: () => void;
  /** Records the first time this load reached a step, for diagnostics. */
  reach: (step: DocumentLoadStep) => void;
  covered: boolean;
  concealed: boolean;
};
const DocumentLoadContext = createContext<LoadMask | null>(null);
export const useDocumentLoadMask = () => useContext(DocumentLoadContext);

/** Mounted with the workbench, including its hidden, prewarmed brand screen. */
export function DocumentLoadMaskProvider({
  pageId,
  maskHost,
  openingId,
  loader,
  children,
}: PropsWithChildren<{
  pageId?: string;
  maskHost?: HTMLElement | null;
  openingId?: string;
  /** Replaces the brand screen in a small surface such as a search preview. */
  loader?: ReactNode;
}>) {
  const maskRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  // What the sidebar lies over of the view's end (over a canvas) is not the
  // screen's: the loading screen centres in the rest, its background still
  // covers it all.
  const endCover = useLiveData(useServiceOptional(ViewService)?.view.endCover$);
  const readyPageRef = useRef<string | null>(null);
  // Clearing a focus selector during editing must not start another load.
  // Each explicit person selection supplies a fresh identity, even on the same doc.
  const openingRef = useRef({ pageId, openingId });
  if (openingRef.current.pageId !== pageId)
    openingRef.current = { pageId, openingId };
  else if (openingId) openingRef.current.openingId = openingId;
  const loadKey = JSON.stringify([pageId, openingRef.current.openingId]);
  const requestedLoadRef = useRef(loadKey);
  requestedLoadRef.current = loadKey;
  const finishRef = useRef<(() => void) | null>(null);
  const [visible, setVisible] = useState(false);
  const [concealed, setConcealed] = useState(false);
  const [longerLoading, setLongerLoading] = useState(false);
  // Children report steps from their own effects, which run before ours, so
  // each load starts its record while rendering.
  const loadRecordRef = useRef<DocumentLoadRecord | null>(null);
  if (loadRecordRef.current?.key !== loadKey)
    loadRecordRef.current = {
      key: loadKey,
      startedAt: performance.now(),
      reached: new Map(),
    };
  const [motionEnabled, setMotionEnabled] = useState(isStreamLainMotionEnabled);
  useLayoutEffect(
    () =>
      subscribeStreamLainMotionPolicy(() =>
        setMotionEnabled(isStreamLainMotionEnabled())
      ),
    []
  );

  useLayoutEffect(() => {
    const mask = maskRef.current;
    const body = bodyRef.current;
    if (!mask || !body) return;
    let disposed = false;
    let releaseTimer: ReturnType<typeof setTimeout> | undefined;
    let noticeTimer: ReturnType<typeof setTimeout> | undefined;
    finishRef.current = null;
    setLongerLoading(false);
    if (!pageId) {
      readyPageRef.current = null;
      mask.hidden = true;
      body.inert = false;
      setVisible(false);
      setConcealed(false);
      return;
    }
    body.inert = !maskHost;
    mask.hidden = false;
    mask.style.opacity = '1';
    setVisible(true);
    setConcealed(true);
    const entered = streamLainAnimationController
      .play(mask, [{ opacity: 0 }, { opacity: 1 }], 'docLoad')
      .finished.then(() => {
        if (!disposed) setConcealed(false);
        return performance.now();
      });
    // Minimum coverage is independent of motion settings: 250ms enter + 250ms hold.
    const started = performance.now();
    let requested = false;
    finishRef.current = () => {
      if (requested || disposed) return;
      requested = true;
      void entered.then(enteredAt => {
        if (disposed) return;
        releaseTimer = setTimeout(
          () => {
            if (disposed) return;
            const exit = streamLainAnimationController.play(
              mask,
              [{ opacity: 1 }, { opacity: 0 }],
              'docLoad'
            );
            void exit.finished.then(() => {
              if (disposed) return;
              mask.hidden = true;
              body.inert = false;
              setVisible(false);
              setLongerLoading(false);
            });
          },
          Math.max(
            0,
            Math.max(started + 500, enteredAt + 250) - performance.now()
          )
        );
      });
    };
    if (readyPageRef.current === loadKey) finishRef.current();
    noticeTimer = setTimeout(() => {
      if (!mask.hidden) setLongerLoading(true);
    }, 20_000);
    return () => {
      disposed = true;
      // Moving the prewarmed screen into its viewport must retain readiness.
      if (requestedLoadRef.current !== loadKey) readyPageRef.current = null;
      finishRef.current = null;
      clearTimeout(releaseTimer);
      clearTimeout(noticeTimer);
      streamLainAnimationController.cancel(mask);
      body.inert = false;
    };
  }, [pageId, maskHost, loadKey]);

  const ready = useCallback(
    (id: string) => {
      if (id === pageId && requestedLoadRef.current === loadKey) {
        readyPageRef.current = loadKey;
        finishRef.current?.();
      }
    },
    [pageId, loadKey]
  );

  const failed = useCallback(() => {
    if (pageId) ready(pageId);
  }, [pageId, ready]);
  const reach = useCallback(
    (step: DocumentLoadStep) => {
      const record = loadRecordRef.current;
      if (record?.key !== loadKey || record.reached.has(step)) return;
      record.reached.set(step, performance.now() - record.startedAt);
    },
    [loadKey]
  );
  const value = useMemo(
    () =>
      pageId
        ? { pageId, ready, failed, reach, covered: visible, concealed }
        : null,
    [pageId, ready, failed, reach, visible, concealed]
  );
  const reportProblem = useDocumentLoadReport(
    longerLoading ? pageId : undefined,
    loadRecordRef
  );
  const mask = (
    <div
      ref={maskRef}
      hidden={!visible}
      aria-hidden={!visible}
      role="status"
      aria-live="polite"
      data-testid="document-load-mask"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 10,
        background: cssVarV2('layer/background/primary'),
        paddingInlineEnd: endCover?.width ?? 0,
        transition: endCover
          ? `padding-inline-end ${endCoverTiming(endCover)}`
          : undefined,
      }}
    >
      {loader && !longerLoading ? (
        loader
      ) : (
        <EditorLoading
          animated={visible && motionEnabled}
          longerLoading={longerLoading}
          onReportProblem={reportProblem}
        />
      )}
    </div>
  );
  return (
    <DocumentLoadContext.Provider value={value}>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          ref={bodyRef}
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            height: '100%',
            minHeight: 0,
            minWidth: 0,
          }}
        >
          {children}
        </div>
        {maskHost ? createPortal(mask, maskHost) : mask}
      </div>
    </DocumentLoadContext.Provider>
  );
}

/**
 * Opens "Report a bug" for a load that takes longer, with the diagnostics of
 * that load. Follows the sync state of the doc while the notice is shown.
 */
function useDocumentLoadReport(
  pageId: string | undefined,
  loadRecordRef: { readonly current: DocumentLoadRecord | null }
) {
  const t = useI18n();
  const helpActions = useServiceOptional(HelpActionsService);
  const workspaceService = useServiceOptional(WorkspaceService);
  const docStateRef = useRef<DocFrontendDocState | null>(null);
  useLayoutEffect(() => {
    docStateRef.current = null;
    if (!pageId || !workspaceService) return;
    const subscription = workspaceService.workspace.engine.doc
      .docState$(pageId)
      .subscribe(state => {
        docStateRef.current = state;
      });
    return () => subscription.unsubscribe();
  }, [pageId, workspaceService]);
  return useMemo(() => {
    if (!pageId || !helpActions) return undefined;
    return () => {
      const record = loadRecordRef.current;
      helpActions.reportBug({
        title: t['com.streamlain.error.loading-timeout-error'](),
        description: t['com.streamlain.error.loading-timeout-description'](),
        errorStack: formatDocumentLoadDiagnostics({
          docId: pageId,
          elapsedMs: record ? performance.now() - record.startedAt : 0,
          reached: record?.reached ?? new Map(),
          online: navigator.onLine,
          docState: docStateRef.current,
        }),
      });
    };
  }, [helpActions, loadRecordRef, pageId, t]);
}
